import React, { useState } from "react";
import { Modal, Stack, Box } from "@mui/material";
import { CloseOutlined } from "@mui/icons-material";
import PrimaryButton from "../../buttons/primary-button";
import SecondaryButton from "../../buttons/secondary-button";
import SecondarySelect from "../../custom-select/secondary-select";
import OutlinedTextarea from "../../custom-input/outlined-textarea";
import RequiredError from "../../required-error";
import ProcessingModal from "../processing-modal";
import CommanErrorModal from "../common-error-modal";
import SupportingDocs from "../../supporting-docs";
import Body2 from "../../typography/body-02";
import { CloseOutlined as RemoveFileIcon } from "@mui/icons-material";
import { validateExceptionRequest } from "../../../utils/required-field-helper/exception-request-required-helper";
import { generateFieldId } from "../../../utils/formId";
import { useSubmitExceptionRequestMutation } from "../../../features/current-loan/currentLoanApiSlice";
import { useErrorMessages } from "../../../utils/error-messages.constants";
import { getConflictPopup, isConflictError } from "../../../utils/conflict-error";
import styles from "./index.module.scss";

const EXCEPTION_TYPE_OPTIONS = [
	{ value: "condition", label: "Condition exception" },
	{ value: "eligibility-pricing", label: "Eligibility / pricing exception" },
	{ value: "other-waiver", label: "Other / guideline waiver" },
];

const FORM_NAME = "exceptionRequest";
const MIN_TEXT_LENGTH = 20;

const isTooShort = (text) => (text || "").trim().length < MIN_TEXT_LENGTH;

// Message for a failed submit: the standard error body, a DRF field error, or a fallback.
const submitErrorText = (err) => {
	if (err?.data?.error?.message) return err.data.error.message;
	if (err?.data?.detail) return err.data.detail;
	const fieldErrors = err?.data && typeof err.data === "object" ? Object.values(err.data) : [];
	const first = fieldErrors.find((value) => Array.isArray(value) && value.length > 0);
	return first ? String(first[0]) : "Unable to submit exception request. Please try again.";
};

// `disabled` (AURA running) blocks the submit; a 409 opens a popup with the backend's message.
const ExceptionRequestModal = ({ open, handleClose, onSubmitted, onSubmit, loanGuid, disabled = false }) => {
	const [exceptionType, setExceptionType] = useState("");
	const [description, setDescription] = useState("");
	const [compensatingFactors, setCompensatingFactors] = useState("");
	const [files, setFiles] = useState([]);
	const [error, setError] = useState("");
	const [processing, setProcessing] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [submitExceptionRequest] = useSubmitExceptionRequestMutation();
	const { getErrorMessage } = useErrorMessages();
	const [conflictPopup, setConflictPopup] = useState(null);

	const formData = { exceptionType, description, compensatingFactors };

	const handleFileAttach = (file) => {
		const incoming = Array.from(file instanceof FileList ? file : [file]);
		setFiles((prev) => [...prev, ...incoming]);
	};

	const handleRemoveFile = (idx) => {
		setFiles((prev) => prev.filter((_, i) => i !== idx));
	};

	const handleSubmit = async () => {
		if (disabled) return;
		if (!validateExceptionRequest(formData, setError)) return;
		setProcessing(true);
		try {
			await submitExceptionRequest({
				loanGuid,
				exceptionType,
				description,
				compensatingFactors,
				fileNames: files.map((f) => f.name),
			}).unwrap();
			setSubmitted(true);
			// Exception now exists — parent marks exceptionStatus "pending" regardless
			// of which post-submit choice the user makes next.
			onSubmitted?.();
		} catch (err) {
			if (isConflictError(err)) setConflictPopup(getConflictPopup(err, getErrorMessage));
			else setError(submitErrorText(err));
		} finally {
			setProcessing(false);
		}
	};

	const handleReset = () => {
		setExceptionType("");
		setDescription("");
		setCompensatingFactors("");
		setFiles([]);
		setError("");
		setSubmitted(false);
	};

	const handleCloseAndReset = () => {
		handleReset();
		handleClose();
	};

	return (
		<>
			<ProcessingModal open={processing} text="Submitting exception request…" />
			<CommanErrorModal
				isOpen={!!conflictPopup}
				onClose={() => setConflictPopup(null)}
				title={conflictPopup?.title}
				message={conflictPopup?.message}
				errorCode={conflictPopup?.code}
			/>
			<Modal
				open={open && !processing}
				aria-labelledby="exception-request-modal"
				disableAutoFocus
				disableEscapeKeyDown
			>
				<Box className={styles.modalBox}>
					<Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
						<span className={styles.modalTitle}>Submit Exception Request</span>
						<CloseOutlined
							className={styles.closeIcon}
							onClick={handleCloseAndReset}
						/>
					</Stack>

					{submitted ? (
						<Stack direction="column" gap={2}>
							<Box className={styles.successBox}>
								<span className={styles.successText}>
									Exception submitted. Loan remains editable. You will be
									notified when the Lock Desk reviews.
								</span>
							</Box>
							<Stack direction="row" gap={1.5}>
								<SecondaryButton
									text="Wait for approval"
									onClick={handleCloseAndReset}
									fullWidth
								/>
								<PrimaryButton
									text="Submit with Open Exception →"
									onClick={() => {
										handleCloseAndReset();
										onSubmit?.("submit");
									}}
									fullWidth
								/>
							</Stack>
						</Stack>
					) : (
						<Stack direction="column" gap={2}>
							<Stack direction="column" gap={0.5}>
								<span className={styles.fieldLabel}>
									Exception type <span className={styles.required}>*</span>
								</span>
								<SecondarySelect
									id={generateFieldId(FORM_NAME, "main", "exceptionType", "select")}
									value={exceptionType}
									handleChange={(e) => setExceptionType(e.target.value)}
									options={EXCEPTION_TYPE_OPTIONS}
									displayEmpty
									fullWidth
									error={!!error && !exceptionType}
								/>
							</Stack>

							<Stack direction="column" gap={0.5}>
								<span className={styles.fieldLabel}>
									Description <span className={styles.required}>*</span>
								</span>
								<OutlinedTextarea
									name={generateFieldId(FORM_NAME, "main", "description", "textarea")}
									value={description}
									handleChange={(e) => setDescription(e.target.value)}
									placeholder="Describe the exception reason (min 20 characters)"
									inputProps={{ maxLength: 500 }}
									rows={4}
									multiline
									fullWidth
									extraClass={`${styles.textarea} ${
										error && isTooShort(description) ? styles.textareaError : ""
									}`}
								/>
								<span className={styles.charCount}>{description.length}/500</span>
							</Stack>

							<Stack direction="column" gap={0.5}>
								<span className={styles.fieldLabel}>
									Compensating factors <span className={styles.required}>*</span>
								</span>
								<OutlinedTextarea
									name={generateFieldId(FORM_NAME, "main", "compensatingFactors", "textarea")}
									value={compensatingFactors}
									handleChange={(e) => setCompensatingFactors(e.target.value)}
									placeholder="List compensating factors (min 20 characters)"
									inputProps={{ maxLength: 500 }}
									rows={4}
									multiline
									fullWidth
									extraClass={`${styles.textarea} ${
										error && isTooShort(compensatingFactors) ? styles.textareaError : ""
									}`}
								/>
								<span className={styles.charCount}>
									{compensatingFactors.length}/500
								</span>
							</Stack>

							<Stack direction="column" gap={0.5}>
								<span className={styles.fieldLabel}>Supporting documentation</span>
								<SupportingDocs
									fileCount={files.length}
									fileTypes={["PDF", "JPG", "PNG"]}
									text="to upload supporting documentation (PDF, JPG, PNG)"
									handleFileAttach={handleFileAttach}
									multiple
								/>
								{files.length > 0 && (
									<Stack direction="column" gap={0.5} mt={1}>
										{files.map((f, idx) => (
											<Stack
												key={`${f.name}-${idx}`}
												direction="row"
												alignItems="center"
												justifyContent="space-between"
												className={styles.fileChip}
											>
												<Body2 text={f.name} />
												<RemoveFileIcon
													className={styles.closeIcon}
													onClick={() => handleRemoveFile(idx)}
												/>
											</Stack>
										))}
									</Stack>
								)}
							</Stack>

							{error && <RequiredError text={error} />}

							<Stack direction="row" gap={1.5} mt={1}>
								<SecondaryButton
									text="Cancel"
									onClick={handleCloseAndReset}
									fullWidth
								/>
								<PrimaryButton
									text="Submit exception →"
									onClick={handleSubmit}
									disabled={disabled}
									fullWidth
									extraClass={styles.submitBtn}
								/>
							</Stack>
						</Stack>
					)}
				</Box>
			</Modal>
		</>
	);
};

export default ExceptionRequestModal;
